import { AlertCircle, CheckCircle, Layers } from 'lucide-react';

const COLORS = {
  milieu: '#3B82F6',
  idea: '#8B5CF6',
  character: '#10B981',
  event: '#F59E0B'
};

const ELEMENT_LABELS = {
  milieu: 'Milieu',
  idea: 'Idea',
  character: 'Character',
  event: 'Event'
};

export default function MICEView({ analysis, onBeatSelect, selectedBeat }) {
  if (!analysis || !analysis.threads) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Layers className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>No M.I.C.E. Quotient analysis available</p>
      </div>
    );
  }

  const { threads, issues } = analysis;

  // Calculate distribution from threads
  const elementDistribution = {};
  threads.forEach(thread => {
    const pages = thread.closePage - thread.openPage;
    elementDistribution[thread.type] = (elementDistribution[thread.type] || 0) + pages;
  });

  const totalPages = Object.values(elementDistribution).reduce((a, b) => a + b, 0);
  const percentages = {};
  Object.keys(elementDistribution).forEach(key => {
    percentages[key] = Math.round((elementDistribution[key] / totalPages) * 100);
  });

  // Find dominant type
  const dominantType = Object.keys(percentages).reduce((a, b) =>
    percentages[a] > percentages[b] ? a : b, Object.keys(percentages)[0]
  );

  return (
    <div className="space-y-8">
      {/* Header with Dominant Type */}
      <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
        <div className="flex items-center space-x-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl"
            style={{ backgroundColor: COLORS[dominantType] }}
          >
            {dominantType.charAt(0).toUpperCase()}
          </div>
          <div>
            <h3 className="text-2xl font-bold text-purple-900">{ELEMENT_LABELS[dominantType]} Story</h3>
            <p className="text-purple-700">
              {percentages[dominantType]}% of your story focuses on {dominantType}
            </p>
          </div>
        </div>
      </div>

      {/* Element Distribution Bar */}
      <div className="bg-white rounded-lg p-6 border">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Layers className="w-5 h-5 mr-2 text-purple-600" />
          Element Distribution
        </h3>
        <div className="h-12 flex rounded-lg overflow-hidden shadow-sm">
          {Object.entries(percentages).map(([type, percent]) => (
            <div
              key={type}
              className="flex items-center justify-center text-white text-sm font-medium"
              style={{
                width: `${percent}%`,
                backgroundColor: COLORS[type]
              }}
              title={`${ELEMENT_LABELS[type]}: ${percent}%`}
            >
              {percent > 10 && `${percent}%`}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-4">
          {Object.entries(COLORS).map(([element, color]) => (
            <div key={element} className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: color }}></div>
              <span className="text-sm text-slate-600">
                {ELEMENT_LABELS[element]} ({percentages[element] || 0}%)
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Story Layer Architecture */}
      <div className="bg-white rounded-lg p-6 border">
        <h3 className="text-lg font-semibold mb-6">Story Layer Architecture</h3>
        <p className="text-sm text-slate-600 mb-4">
          M.I.C.E. threads should nest properly - the first opened should be the last closed (mirror structure).
        </p>
        <div className="space-y-4">
          {threads.map((thread, index) => (
            <div key={index} className="flex items-center space-x-4">
              <div className="w-16 text-sm text-slate-500 text-right">Level {index + 1}</div>
              <div className="flex-1">
                <div
                  className="relative rounded-lg p-4 text-white shadow-sm"
                  style={{
                    backgroundColor: COLORS[thread.type],
                    marginLeft: `${index * 24}px`,
                    marginRight: `${index * 24}px`
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-semibold text-lg">{ELEMENT_LABELS[thread.type].toUpperCase()}</span>
                      <span className="ml-3 text-sm opacity-90">
                        Opens: p{thread.openPage} → Closes: p{thread.closePage}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-sm opacity-90 mt-1">{thread.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Issues / Health Check */}
      {issues && issues.length > 0 && (
        <div className="bg-white rounded-lg p-6 border">
          <h3 className="text-lg font-semibold mb-4">M.I.C.E. Structure Health</h3>
          <div className="space-y-3">
            {issues.map((issue, index) => (
              <div key={index} className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-slate-700">{issue}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Element Type Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(COLORS).map(([element, color]) => (
          <div key={element} className="bg-white rounded-lg p-4 border">
            <div
              className="h-2 w-full rounded mb-3"
              style={{ backgroundColor: color }}
            />
            <h4 className="font-semibold capitalize mb-2">{ELEMENT_LABELS[element]}</h4>
            <div className="text-sm text-slate-600 space-y-1">
              <p><strong>{percentages[element] || 0}%</strong> of story</p>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {element === 'milieu' && 'World & setting exploration'}
              {element === 'idea' && 'Questions & information seeking'}
              {element === 'character' && 'Personal transformation'}
              {element === 'event' && 'Problem-solving & action'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
